import React, { useCallback, useRef, useState } from 'react';
import { useDismissable } from './useDismissable';

export interface CounterConfig {
  key: string;
  label: string;
  min: number;
  max?: number;
}

interface CounterPopoverFieldProps {
  id: string;
  counters: CounterConfig[];
  values: Record<string, number>;
  onChange: (key: string, value: number) => void;
  /** Text shown on the closed trigger, e.g. "2 pasajeros". */
  previewText: string;
  triggerLabel: string;
  disabled?: boolean;
  hasError?: boolean;
  errorId?: string;
  /** Extra controls rendered under the counters (e.g. children ages). */
  extraContent?: React.ReactNode;
}

function clamp(value: number, counter: CounterConfig): number {
  const upper = counter.max ?? Number.POSITIVE_INFINITY;
  return Math.min(Math.max(value, counter.min), upper);
}

/**
 * Number-of-travellers style field: a control that reads like the rest of the
 * search card and opens a small panel of +/- counters.
 */
export const CounterPopoverField: React.FC<CounterPopoverFieldProps> = ({
  counters,
  disabled = false,
  errorId,
  extraContent,
  hasError = false,
  id,
  onChange,
  previewText,
  triggerLabel,
  values,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useDismissable(isOpen, containerRef, close);

  return (
    <div className='searcher-field__anchor' ref={containerRef}>
      <button
        aria-describedby={hasError && errorId ? errorId : undefined}
        aria-expanded={isOpen}
        aria-haspopup='dialog'
        aria-label={triggerLabel}
        className={['searcher-field__control', hasError && 'searcher-field__control--invalid']
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
        id={id}
        onClick={() => setIsOpen((open) => !open)}
        type='button'
      >
        <span className='searcher-field__control-text'>{previewText}</span>
      </button>

      {isOpen && (
        <div
          aria-label={triggerLabel}
          className='searcher-popover searcher-popover--panel'
          role='dialog'
        >
          {counters.map((counter) => {
            const value = values[counter.key] ?? counter.min;
            const atMin = value <= counter.min;
            const atMax = counter.max != null && value >= counter.max;

            return (
              <div className='searcher-counter' key={counter.key}>
                <span className='searcher-counter__label'>{counter.label}</span>
                <span className='searcher-counter__controls'>
                  <button
                    aria-label={`Quitar ${counter.label}`}
                    className='searcher-counter__button'
                    disabled={atMin}
                    onClick={() => onChange(counter.key, clamp(value - 1, counter))}
                    type='button'
                  >
                    −
                  </button>
                  <span aria-live='polite' className='searcher-counter__value'>
                    {value}
                  </span>
                  <button
                    aria-label={`Agregar ${counter.label}`}
                    className='searcher-counter__button'
                    disabled={atMax}
                    onClick={() => onChange(counter.key, clamp(value + 1, counter))}
                    type='button'
                  >
                    +
                  </button>
                </span>
              </div>
            );
          })}
          {extraContent}
        </div>
      )}
    </div>
  );
};
